import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, ShieldCheck, Star, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { deleteVoice, fmtDate, patchVoice, selectVoice } from "@/lib/myVoicesApi";
import { safeApiError } from "@/lib/safeErrorText";

const pill = "inline-flex items-center gap-1.5 rounded-full border border-white/12 bg-white/[0.05] px-3 py-1.5 text-[12px] font-medium text-white/80 transition-colors hover:bg-white/[0.12]";

/** Selected badge / use-this-voice, rename, consent record and delete. */
export default function VoiceHeader({ voice, onChanged }) {
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState(false);
  const run = async (fn, ok) => { try { await fn(); toast.success(ok); onChanged(); } catch (e) { toast.error("Didn't work", { description: safeApiError(e) }); } };
  const rename = () => {
    const name = window.prompt("Voice name", voice.name);
    if (name && name.trim() && name.trim() !== voice.name) run(() => patchVoice(voice.id, { name: name.trim() }), "Renamed");
  };
  const remove = async () => {
    try { await deleteVoice(voice.id); toast.success("Voice deleted"); navigate("/create/my-voices"); }
    catch (e) { toast.error("Couldn't delete", { description: safeApiError(e) }); }
  };
  return (
    <div data-testid="voice-header" className="flex flex-wrap items-center gap-2">
      {voice.selected ? (
        <span data-testid="voice-selected-badge" className="inline-flex items-center gap-1.5 rounded-full bg-cyan-300 px-3 py-1.5 text-[12px] font-semibold text-black"><Star className="h-3.5 w-3.5 fill-black" /> The studio sings with this voice</span>
      ) : (
        <button type="button" data-testid="voice-select" className={pill} onClick={() => run(() => selectVoice(voice.id), "This voice is now selected")}><Star className="h-3.5 w-3.5" /> Use this voice</button>
      )}
      <button type="button" data-testid="voice-rename" className={pill} onClick={rename}><Pencil className="h-3.5 w-3.5" /> Rename</button>
      <span data-testid="voice-consent" title={voice.consent?.statement} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] text-emerald-200/80">
        <ShieldCheck className="h-3.5 w-3.5" /> Consent signed by {voice.consent?.signer} · {fmtDate(voice.consent?.accepted_at)}
      </span>
      <button type="button" data-testid="voice-delete" className={`${pill} ml-auto hover:border-rose-400/40 hover:text-rose-200`} onClick={() => setConfirm(true)}><Trash2 className="h-3.5 w-3.5" /> Delete voice</button>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="border-white/10 bg-[#0b0e1c] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{voice.name}” for good?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">Removes the voice, every trained model, all previews and the cleaned training recordings. Your original Enhanced vocals and studio takes are not touched.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="voice-delete-cancel" className="border-white/15 bg-transparent text-white hover:bg-white/10">Keep it</AlertDialogCancel>
            <AlertDialogAction data-testid="voice-delete-confirm" onClick={remove} className="bg-rose-500 text-white hover:bg-rose-400">Delete everything</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
